/**
 * MapBoundary — the ONLY component that renders a Google map (D13).
 *
 * ── THE CLASS THIS CLOSES ───────────────────────────────────────────────────
 *
 * A Google map is a BILLABLE side effect: mounting `<APIProvider>` injects the
 * Maps JS script, and mounting `<Map>` is one Dynamic Maps load. Every map in
 * this app used to attach that side effect to MOUNT — so a listing detail page
 * whose 300 px location map sat below the fold fired 22 `maps.googleapis.com`
 * requests at `scrollY = 0`, and every JS-executing client, crawlers included,
 * paid one map load per page it opened whether or not anybody looked at the
 * map. The owner saw the consequence as the "for development purposes only"
 * watermark (O-9).
 *
 * The side effect now belongs to VIEW. Until the boundary is in (or within one
 * viewport of) the viewport — or, for `load="tap"`, until the reader asks for
 * the map — this renders a static, request-free placeholder the SAME SIZE as
 * the map: a neutral tile and, when the caller has a coordinate, an inline SVG
 * pin at its centre (every caller centres its map on that coordinate). The
 * placeholder is also what the SERVER renders: `live` starts `false` on both
 * sides, so hydration cannot flip it, and the SSR HTML carries no map script.
 *
 * ── WHY "ONLY" IS A GATE AND NOT A CONVENTION ───────────────────────────────
 *
 * `Map` and `APIProvider` are importable from the map seam (`./vis-gl`) by THIS
 * FILE and no other, and from the package by the seam alone — the
 * `[map-boundary]` rows in `apps/public/eslint.config.mjs`. A new map is
 * therefore lazy by construction: the only way to render one is through here.
 *
 * ── HOW IT DECIDES ──────────────────────────────────────────────────────────
 *
 *   * `load="in-view"` — an IntersectionObserver with a `rootMargin` of one
 *     viewport, so the map is usually painted by the time it scrolls in. An
 *     element that is not rendered (`display: none`, e.g. a pane a breakpoint
 *     hides) never intersects, so it never loads. Where the observer does not
 *     exist the map renders at once: a browser without it is a browser we
 *     cannot be lazy in, and a missing map is worse than an eager one.
 *   * `load="tap"` — a "Show map" button over the placeholder. For a map that
 *     would otherwise be a FROZEN picture (the listing detail's location map),
 *     the picture needs no SDK; the tap opens the live, interactive map in
 *     place and moves focus onto it.
 *   * `defer` — the caller is not ready to mount the map yet (the listings map
 *     measures its pane before it can compute `defaultCenter`, which Google
 *     reads once). The placeholder holds until it is.
 *
 * Once live, a map stays live: scrolling it away again does not unmount it —
 * that would pay for a second load.
 *
 * ── WHEN GOOGLE CANNOT BE REACHED (#7696) ────────────────────────────────────
 *
 * The phases are a typed state machine (`./map-phase`): `placeholder`, `live`,
 * and `unavailable` — the `MapUnavailable` variant the boundary enters
 * when `<APIProvider onError>` reports that the Maps JS SDK did not load. The
 * browser never received `maps.googleapis.com`: ~13% of estate views and 21%
 * of zh-CN ones (relay census, 2026-09-25 — mainland visitors, blocking
 * networks and extensions, crawlers). Before this phase the live map's
 * container stayed EMPTY for as long as the reader looked at it.
 *
 * `unavailable` renders a Google-FREE fallback in the map's own box: the
 * caller's structured address and district (`place`), and an "open in maps"
 * LINK at the pin — a link loads nothing, so it works exactly where the script
 * did not. No Google Static Maps image (same blocked origin family) and no
 * spinner: the fallback is final, because a script that failed to load does
 * not load on a retry.
 *
 * A script that HANGS — a blackholed connection never fires `error` — enters
 * the same phase at a deadline (#8204): `LiveMap` arms
 * `MAPS_SCRIPT_LOAD_DEADLINE_MS` when it mounts `<APIProvider>` (which inserts
 * the script tag) and disarms it on `onLoad` / `onError`, so a page behind a
 * silent block reaches the fallback and is COUNTED (`script_load_timeout`),
 * instead of an empty box that sat in the failure ratio's denominator only.
 *
 * ── A CRAWLER'S IN-VIEW MAP NEVER ARMS (#7547) ───────────────────────────────
 *
 * A rendering crawler lays the page out in a tall, never-scrolled viewport, so
 * every `in-view` map's one-viewport margin intersects on first layout and the
 * crawler paid a billable load per page. The boundary classifies the UA on the
 * CLIENT, with the ONE roster the maps report already uses (`mapsAudience`,
 * `@letsgethome/lib/bots`), and a crawler's in-view observer never arms.
 *
 * Client-side ON PURPOSE: the public HTML is shared-cached with no
 * `Vary: User-Agent`, so a per-UA verdict baked into SSR would hand one
 * crawler's map-less page to every person behind it (and vice versa). The flag
 * starts `false` on both sides and flips in an effect, so the placeholder
 * markup never differs and hydration cannot mismatch. `defer` and the tap
 * button are untouched (a crawler does not tap).
 */
import { useSeen } from '@/shared/hooks/useSeen';
import { useTranslations } from '@/shared/i18n/useTranslations';
import {
  mapsAudience,
  observeMapsWatermark,
  reportMapsFailure,
  reportMapsScriptLoad,
} from '@/shared/lib/maps-health';
import { useConfig } from '@/shared/providers/ConfigProvider';
import { MAPS_SCRIPT_LOAD_DEADLINE_MS } from '@letsgethome/lib/telemetry/transport';
import type { PlaintextString } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { cn } from '@letsgethome/ui/lib/utils';
import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from 'react';

import { GOOGLE_MAPS_MAP_ID } from './map-config';
import {
  INITIAL_MAP_PHASE,
  nextMapPhase,
  openInMapsHref,
  resolveMapView,
  UNAVAILABLE_CAUSE_OF,
  type ScriptLoadEndedEvent,
} from './map-phase';
import { APIProvider, Map, type MapCameraChangedEvent } from './vis-gl';

/**
 * The focus indicator of the two boundary boxes that take focus
 * programmatically (the live map after a tap, the fallback after a failed
 * tap). `focus-visible`, so a keyboard user who pressed "Show map" sees where
 * focus went (WCAG 2.4.7) while a pointer tap paints nothing; the design
 * system's own ring (`@letsgethome/ui` button/input), spelled once here.
 */
const FOCUS_RING = 'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

/** When a boundary mounts its live map. */
export type MapLoad = 'in-view' | 'tap';

/**
 * How far outside the viewport an `in-view` boundary starts loading — one
 * viewport height above and below, so a reader scrolling at a normal pace
 * meets a painted map rather than a placeholder.
 */
export const MAP_BOUNDARY_ROOT_MARGIN = '100% 0px';

/** The placeholder's own identity, for tests that must tell it from the map. */
export const MAP_PLACEHOLDER_TESTID = 'map-placeholder';

/** The Google-free fallback's identity (the `unavailable` phase, #7696). */
export const MAP_UNAVAILABLE_TESTID = 'map-unavailable';

/**
 * What the map SHOWS, in words — rendered by the `unavailable` fallback when
 * the Maps SDK cannot load. Both fields are already on every page that has a
 * map (the estate header's street address and district, the listing's full
 * address); a map with neither (the many-pin listings map) falls back to its
 * label and nothing else.
 */
export interface MapPlace {
  /** The structured street address (data, shown verbatim). */
  address?: PlaintextString;
  /** The district name (data, shown verbatim). */
  district?: PlaintextString;
}

interface LatLng {
  lat: number;
  lng: number;
}

/**
 * The `<Map>` options a caller may set — the ones this app uses, typed here
 * rather than borrowed from the library, whose `MapProps` is written against
 * `google.maps.*` ambient types this app's type-checker does not see. `mapId`
 * and the sizing class are the boundary's own, never the caller's.
 */
export interface MapBoundaryMapOptions {
  defaultCenter: LatLng;
  defaultZoom: number;
  gestureHandling: 'none' | 'cooperative' | 'greedy';
  disableDefaultUI: boolean;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  streetViewControl?: boolean;
  fullscreenControl?: boolean;
  onDragstart?: () => void;
  onZoomChanged?: () => void;
  onCameraChanged?: (event: MapCameraChangedEvent) => void;
}

export interface MapBoundaryProps {
  /** When the live map mounts. */
  load: MapLoad;
  /** The accessible name of the map (and of its placeholder). */
  label: string;
  /** The live map's options. */
  map: MapBoundaryMapOptions;
  /** A coordinate to draw as the placeholder's pin; omit for a many-pin map. */
  pin?: LatLng;
  /** Hold the placeholder even when in view (see the header's `defer`). */
  defer?: boolean;
  /** Classes on the boundary box. It fills its parent by default. */
  className?: string;
  /** The address and district the fallback shows if the SDK cannot load. */
  place?: MapPlace;
  /** The map's children — markers. Rendered only once the map is live. */
  children?: ReactNode;
}

export function MapBoundary({
  load,
  label,
  map,
  pin,
  defer = false,
  className,
  place,
  children,
}: MapBoundaryProps) {
  // A crawler's in-view observer never arms (see the header). `false` on the
  // server and on the first client render, so hydration agrees; and the first
  // commit cannot arm the observer anyway (its box ref is not set yet).
  const [isCrawler, setIsCrawler] = useState(false);
  useEffect(() => setIsCrawler(mapsAudience().is_crawler === 'true'), []);
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [phase, dispatch] = useReducer(nextMapPhase, INITIAL_MAP_PHASE);
  const seen = useSeen(box, load === 'in-view' && !isCrawler, MAP_BOUNDARY_ROOT_MARGIN);
  const view = resolveMapView(phase, { seenInView: load === 'in-view' && seen, defer });

  let body: ReactNode;
  switch (view) {
    case 'live':
      body = (
        <LiveMap label={label} map={map} focusOnMount={load === 'tap'} onScriptLoadEnded={dispatch}>
          {children}
        </LiveMap>
      );
      break;
    case 'unavailable':
      body = (
        <MapFallback
          label={label}
          href={pin ? openInMapsHref(pin) : undefined}
          address={place?.address}
          district={place?.district}
          focusOnMount={load === 'tap'}
        />
      );
      break;
    case 'placeholder':
      body = (
        <MapPlaceholder
          label={label}
          pin={pin}
          onShow={load === 'tap' ? () => dispatch({ kind: 'requested' }) : undefined}
        />
      );
      break;
  }

  return (
    <div ref={setBox} className={cn('relative h-full w-full', className)}>
      {body}
    </div>
  );
}

// ----------------------------------------------------------------------------
// The live map — the one place the SDK is bootstrapped
// ----------------------------------------------------------------------------

/**
 * The SDK load's outcome, decided ONCE: the first of `onLoad`, `onError` and
 * the deadline wins and the others are ignored — so a hung request that errors
 * after the deadline is not a second report, and a load that lands after it
 * does not resurrect a map the reader has already been given a fallback for.
 * Armed after `<APIProvider>`'s own mount effect (a child's effects run first),
 * i.e. once the script tag is in the document. `onEnded` must be stable (it is
 * the boundary's `dispatch`): the provider captures its callbacks on mount.
 */
function useScriptLoadOutcome(onEnded: (event: ScriptLoadEndedEvent) => void) {
  const settled = useRef(false);
  const deadline = useRef<ReturnType<typeof setTimeout>>(undefined);
  const settle = useCallback(
    (event?: ScriptLoadEndedEvent, context?: Record<string, unknown>) => {
      if (settled.current) return;
      settled.current = true;
      clearTimeout(deadline.current);
      if (!event) return;
      reportMapsFailure(UNAVAILABLE_CAUSE_OF[event.kind], context);
      onEnded(event);
    },
    [onEnded]
  );
  useEffect(() => {
    deadline.current = setTimeout(
      () => settle({ kind: 'script_load_timed_out' }),
      MAPS_SCRIPT_LOAD_DEADLINE_MS
    );
    return () => clearTimeout(deadline.current);
  }, [settle]);
  return settle;
}

function LiveMap({
  label,
  map,
  focusOnMount,
  onScriptLoadEnded,
  children,
}: {
  label: string;
  map: MapBoundaryMapOptions;
  focusOnMount: boolean;
  onScriptLoadEnded: (event: ScriptLoadEndedEvent) => void;
  children?: ReactNode;
}) {
  // The key is REQUIRED and branded (`GoogleMapsApiKey`, minted only by
  // `server/config.ts`), so there is deliberately no empty-key branch (ADR
  // 0142): a keyless map is unrepresentable.
  const { googleMapsApiKey } = useConfig();
  const region = useRef<HTMLDivElement>(null);
  const settle = useScriptLoadOutcome(onScriptLoadEnded);

  // Watch for the "for development purposes only" watermark while a map is
  // mounted (Google exposes no callback for it). gm_authFailure and the script
  // onError below cover the other two failure modes; see maps-health.ts.
  useEffect(() => observeMapsWatermark(), []);

  // The denominator of the per-locale script-load failure ratio: this page
  // load began loading the SDK (deduped per page load in maps-health.ts).
  useEffect(() => reportMapsScriptLoad(), []);

  // A tap replaced the button the reader was focused on; hand focus to the
  // map it opened rather than dropping it on <body>.
  useEffect(() => {
    if (focusOnMount) region.current?.focus();
  }, [focusOnMount]);

  return (
    <div
      ref={region}
      role="region"
      aria-label={label}
      tabIndex={focusOnMount ? -1 : undefined}
      className={cn('h-full w-full', FOCUS_RING)}
    >
      <APIProvider
        apiKey={googleMapsApiKey}
        onLoad={() => settle()}
        onError={(error: unknown) => settle({ kind: 'script_load_failed' }, { error })}
      >
        <Map {...map} mapId={GOOGLE_MAPS_MAP_ID} className="h-full w-full">
          {children}
        </Map>
      </APIProvider>
    </div>
  );
}

// ----------------------------------------------------------------------------
// The fallback — the `unavailable` phase, Google-free, the map's own size
// ----------------------------------------------------------------------------

/** A static map pin glyph. Decorative: the name is the container's. */
function PinGlyph({ className }: { className: string }) {
  return (
    <svg aria-hidden="true" focusable="false" viewBox="0 0 24 32" className={className}>
      <path
        fill="currentColor"
        d="M12 0C5.37 0 0 5.2 0 11.6 0 20.3 12 32 12 32s12-11.7 12-20.4C24 5.2 18.63 0 12 0Z"
      />
      <circle cx="12" cy="11.5" r="4.5" className="fill-background" />
    </svg>
  );
}

/**
 * What a reader sees when Google's script cannot load (#7696): the address,
 * the district, and an "open in maps" link at the pin — everything the map
 * would have told them except the picture, and nothing that needs Google to
 * have loaded. No spinner: this is final for the page (see `./map-phase`).
 * The phase is carried on `data-map-phase` so a test can read the variant
 * rather than infer it from markup.
 */
function MapFallback({
  label,
  href,
  address,
  district,
  focusOnMount,
}: {
  label: string;
  /** The open-in-maps link, when the map has one pin to aim it at. */
  href?: string;
  address?: PlaintextString;
  district?: PlaintextString;
  /**
   * The boundary was opened by a TAP, so focus sat inside the live map's
   * region — which this fallback just replaced. Without a hand-off it would
   * fall to `<body>`; take it here, as the live map took it from the button.
   */
  focusOnMount: boolean;
}) {
  // The link copy is the estate gallery's existing "View on Google Maps"
  // string (web_public): the fallback needs no new string.
  const t = useTranslations('estates.detail.gallery');
  const group = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusOnMount) group.current?.focus();
  }, [focusOnMount]);
  return (
    <div
      ref={group}
      data-testid={MAP_UNAVAILABLE_TESTID}
      data-map-phase="unavailable"
      role="group"
      aria-label={label}
      tabIndex={-1}
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-2 bg-muted p-6 text-center',
        FOCUS_RING
      )}
    >
      <PinGlyph className="h-8 w-6 text-primary" />
      {address ? (
        <p data-map-place="address" className="font-medium text-foreground">
          {address}
        </p>
      ) : null}
      {district ? (
        <p data-map-place="district" className="text-sm text-muted-foreground">
          {district}
        </p>
      ) : null}
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-primary underline underline-offset-2"
        >
          {t('viewOnGoogleMaps')}
        </a>
      ) : null}
    </div>
  );
}

// ----------------------------------------------------------------------------
// The placeholder — static, request-free, the map's own size
// ----------------------------------------------------------------------------

function MapPlaceholder({
  label,
  pin,
  onShow,
}: {
  label: string;
  pin?: LatLng;
  onShow?: () => void;
}) {
  const t = useTranslations('common');
  // The pin's TIP sits on the tile centre, where the live map will put the
  // coordinate. Decorative: the name is the container's.
  const pinMark = pin ? (
    <PinGlyph className="absolute left-1/2 top-1/2 h-8 w-6 -translate-x-1/2 -translate-y-full text-primary drop-shadow" />
  ) : null;

  if (onShow) {
    return (
      <div
        data-testid={MAP_PLACEHOLDER_TESTID}
        role="group"
        aria-label={label}
        className="relative h-full w-full bg-muted"
      >
        {pinMark}
        <div className="absolute inset-x-0 bottom-4 flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full shadow-md"
            onClick={onShow}
          >
            {t('showMap')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={MAP_PLACEHOLDER_TESTID}
      role="img"
      aria-label={label}
      className="relative h-full w-full bg-muted"
    >
      {pinMark}
    </div>
  );
}
